import { FC, useMemo } from 'react'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import R4RIcon from 'components/R4RIcon'
import {
  useLeaseContractsList,
  LeaseContractResponse
} from 'services/contracts/hooks/useLeaseContracts'
import { useLeaseContractsRentAccountBalances } from 'services/contracts/hooks/useLeaseContractsRentAccountBalance'
import { CONTRACT_ACTIVE_GROUP } from 'services/contracts/contracts.consts'

export interface LeaseContractWithBalance extends LeaseContractResponse {
  // Backend/CRM convention: negative = debt, positive = overpayment.
  balance: number
  hasDebt: boolean
}

const isActiveLease = ({ leaseStatus }: LeaseContractResponse) =>
  CONTRACT_ACTIVE_GROUP.includes(leaseStatus)

const newestLease = <T extends LeaseContractResponse>(leases: T[]) =>
  leases.reduce((max, lease) => (lease.id > max.id ? lease : max), leases[0])

const mostIndebtedLease = (leases: LeaseContractWithBalance[]) =>
  leases.reduce((worst, lease) => (lease.balance < worst.balance ? lease : worst), leases[0])

// Contract the "Go to invoices" button should open:
// 1. active contract with debt (largest debt if several),
// 2. any contract with debt (largest debt),
// 3. active contract, 4. newest contract.
export const selectBalanceLease = (
  leases: LeaseContractWithBalance[]
): LeaseContractWithBalance | null => {
  if (!leases.length) {
    return null
  }

  const leasesWithDebt = leases.filter(({ hasDebt }) => hasDebt)
  const activeLeasesWithDebt = leasesWithDebt.filter(isActiveLease)

  if (activeLeasesWithDebt.length) {
    return mostIndebtedLease(activeLeasesWithDebt)
  }

  if (leasesWithDebt.length) {
    return mostIndebtedLease(leasesWithDebt)
  }

  const activeLeases = leases.filter(isActiveLease)

  return activeLeases.length ? newestLease(activeLeases) : newestLease(leases)
}

const WidgetBalance: FC = () => {
  const navigate = useNavigate()
  const { formatMessage, formatNumber } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { leaseContractsList } = useLeaseContractsList()

  const allLeases = useMemo(() => leaseContractsList || [], [leaseContractsList])
  const leaseIds = useMemo(() => allLeases.map(({ id }) => id), [allLeases])

  // Saldo każdej umowy z backendu (to samo źródło co CRM), nie liczone z dokumentów.
  const { balances, isLoading } = useLeaseContractsRentAccountBalances(leaseIds)

  const contractsWithBalances = useMemo<LeaseContractWithBalance[]>(
    () =>
      allLeases.map((lease) => {
        const balance = balances[lease.id] ?? 0

        return { ...lease, balance, hasDebt: balance < 0 }
      }),
    [allLeases, balances]
  )

  const totalBalance = useMemo(
    () => contractsWithBalances.reduce((sum, { balance }) => sum + balance, 0),
    [contractsWithBalances]
  )

  const balanceLease = useMemo(
    () => selectBalanceLease(contractsWithBalances),
    [contractsWithBalances]
  )

  if (!allLeases.length) {
    return null
  }

  const hasManyLeases = contractsWithBalances.length > 1
  // Contract opened by the "Go to invoices" button (hidden when operations are frozen).
  const invoicesLeaseId =
    balanceLease && !balanceLease.isOperationsFrozen ? balanceLease.id : null

  const formatBalance = (value: number) => {
    if (value === 0) {
      return formatMessage({ id: 'balanceWidget.paid', defaultMessage: 'Opłacone' })
    }

    const amount = formatNumber(Math.abs(value), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    return `${value < 0 ? '-' : '+'}${amount} PLN`
  }

  const balanceLabel = formatBalance(totalBalance)

  const goToInvoices = (leaseId: number) =>
    navigate(`additional-services/${leaseId}/payment-schedule`)

  const invoicesButtonLabel = formatMessage({
    id: 'balanceWidget.goToInvoices',
    defaultMessage: 'Przejdź do faktur'
  })

  // Saldo per umowa – pokazywane tylko gdy umów jest więcej niż jedna, żeby było jasne,
  // że duża kwota jest sumą i której umowy dotyczy dług.
  const balanceBreakdown = hasManyLeases && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: '8px' }}>
      <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px' }}>
        {formatMessage({
          id: 'balanceWidget.totalForContracts',
          defaultMessage: 'Łącznie dla wszystkich umów'
        })}
      </Typography>
      {contractsWithBalances.map((lease) => {
        const clickable = !lease.isOperationsFrozen

        return (
          <ButtonBase
            key={lease.id}
            disabled={!clickable}
            onClick={() => goToInvoices(lease.id)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px',
              width: '100%',
              fontFamily: theme.typography.fontFamily,
              fontSize: '13px',
              color: '#fff',
              opacity: lease.hasDebt ? 1 : 0.7,
              textAlign: 'left'
            }}>
            <Box component='span' sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {lease.contractNumber}
            </Box>
            <Box component='span' sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
              {formatBalance(lease.balance)}
            </Box>
          </ButtonBase>
        )
      })}
    </Box>
  )

  if (isDesktop) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '36px 40px',
          background: theme.r4r.gradient,
          boxShadow: theme.r4r.heroShadow
        }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
          <Typography
            sx={{
              color: '#fff',
              opacity: 0.55,
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase'
            }}>
            {formatMessage({
              id: 'balanceWidget.yourBalance',
              defaultMessage: 'Twoje saldo'
            })}
          </Typography>
          {isLoading ? (
            <CircularProgress size={44} sx={{ color: '#fff' }} />
          ) : (
            <>
              <Typography
                sx={{ color: '#fff', fontSize: '44px', fontWeight: 800, lineHeight: 'normal' }}>
                {balanceLabel}
              </Typography>
              {balanceBreakdown}
            </>
          )}
        </Box>
        {invoicesLeaseId !== null && (
          <Button
            onClick={() => goToInvoices(invoicesLeaseId)}
            startIcon={<R4RIcon name='receipt' size={18} />}
            sx={{
              backgroundColor: theme.r4r.yellow,
              color: theme.r4r.navy,
              fontSize: '15px',
              fontWeight: 700,
              padding: '14px 28px',
              borderRadius: 0,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#e5b500' }
            }}>
            {invoicesButtonLabel}
          </Button>
        )}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        padding: '24px 20px 20px',
        background: theme.r4r.gradient,
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.55)',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.8px',
          textTransform: 'uppercase'
        }}>
        {formatMessage({
          id: 'balanceWidget.yourBalance',
          defaultMessage: 'Twoje saldo'
        })}
      </Typography>
      {isLoading ? (
        <CircularProgress size={36} sx={{ color: '#fff', my: '8px' }} />
      ) : (
        <>
          <Typography
            sx={{ color: '#fff', fontSize: '32px', fontWeight: 800, lineHeight: 'normal', mt: '4px' }}>
            {balanceLabel}
          </Typography>
          {!!balanceLease && !hasManyLeases && (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', mt: '4px' }}>
              {formatMessage(
                {
                  id: 'balanceWidget.appliesToContract',
                  defaultMessage: 'Dotyczy umowy {contractNumber}'
                },
                { contractNumber: balanceLease.contractNumber }
              )}
            </Typography>
          )}
          {balanceBreakdown}
        </>
      )}
      {invoicesLeaseId !== null && (
        <Button
          onClick={() => goToInvoices(invoicesLeaseId)}
          fullWidth
          startIcon={<R4RIcon name='receipt' size={18} />}
          sx={{
            mt: '16px',
            backgroundColor: theme.r4r.yellow,
            color: theme.r4r.navy,
            fontSize: '13px',
            fontWeight: 700,
            padding: '5px 12px',
            minHeight: '28px',
            borderRadius: 0,
            '&:hover': { backgroundColor: '#e5b500' }
          }}>
          {invoicesButtonLabel}
        </Button>
      )}
    </Box>
  )
}

export default WidgetBalance
